import { FC, ReactNode } from 'react';
import { Box, Button, IconButton, Snackbar, SnackbarOrigin } from '@mui/material';
import { Close } from '@mui/icons-material';

export interface StyledSnackBarProps {
  open: boolean;
  message?: string | null;
  icon: ReactNode;
  color: string;
  actionLabel?: string;
  onAction?: () => void;
  onClose: () => void;
  autoHideDuration?: number | null;
  anchorOrigin?: SnackbarOrigin;
  closeAriaLabel?: string;
  /** When true, only auto-hide timeout closes the snackbar (clickaway ignored). */
  closeOnTimeoutOnly?: boolean;
}

const DEFAULT_ANCHOR: SnackbarOrigin = { vertical: 'bottom', horizontal: 'right' };

const StyledSnackBar: FC<StyledSnackBarProps> = ({
  open,
  message,
  icon,
  color,
  actionLabel,
  onAction,
  onClose,
  autoHideDuration = 4000,
  anchorOrigin = DEFAULT_ANCHOR,
  closeAriaLabel = 'close',
  closeOnTimeoutOnly = false,
}) => {
  const handleActionClick = () => {
    onAction?.();
    onClose();
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={(_, reason) => {
        if (closeOnTimeoutOnly) {
          if (reason === 'timeout') onClose();
          return;
        }
        onClose();
      }}
      anchorOrigin={anchorOrigin}
    >
      <Box
        sx={{
          display: 'flex',
          borderRadius: '10px',
          overflow: 'hidden',
          minWidth: '340px',
          maxWidth: '480px',
          boxShadow: '0 8px 28px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.16)',
        }}
      >
        <Box
          sx={{
            background: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            color: '#fff',
            '& > svg': { width: 20, height: 20 },
          }}
        >
          {icon}
        </Box>
        <Box
          sx={{
            background: '#1c1c1c',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            padding: '10px 12px 10px 16px',
            flex: 1,
            gap: '4px',
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 400, flex: 1, lineHeight: 1.4 }}>
            {message}
          </span>
          {!!actionLabel && !!onAction && (
            <Button
              size="small"
              onClick={handleActionClick}
              sx={{
                color: '#ffffff',
                fontWeight: 400,
                fontSize: '13px',
                textDecoration: 'underline',
                border: 'none',
                minWidth: 'unset',
                padding: '0 6px',
                whiteSpace: 'nowrap',
                '&:hover': { background: 'transparent', textDecoration: 'underline' },
              }}
            >
              {actionLabel}
            </Button>
          )}
          <IconButton
            size="small"
            aria-label={closeAriaLabel}
            onClick={onClose}
            sx={{ color: '#ffffff', padding: '2px', flexShrink: 0 }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    </Snackbar>
  );
};

export default StyledSnackBar;
